'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import SdHeaderDrawerFromLeft from '@/components/layouts/SdHeaderDrawerFromLeft';
import { useGlobal } from '@/contexts/GlobalContext';

interface SdHeaderMenuDrawerContextProps {
  isOpen: boolean;
  openDrawer: (content: ReactNode) => void;
  closeDrawer: () => void;
}

const SdHeaderMenuDrawerContext = createContext<SdHeaderMenuDrawerContextProps | undefined>(undefined);

export const SdHeaderMenuDrawerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = useGlobal();

  const [isOpen, setIsOpen] = useState(false);
  const [drawerComponent, setDrawerComponent] = useState<React.ReactNode | null>(null);

  const openDrawer = (component: React.ReactNode) => {
    setDrawerComponent(component);
    setIsOpen(true);
  };

  const closeDrawer = async () => {
    setIsOpen(false);
    // NOTE: ドロワーのアニメーションが効かない対策
    // フラグ変更 -> ドロワー内容（コンポーネント）NULL -> レンダリング おそらくこの順で
    // 処理されており、アニメーションが効く前にコンポーネントDOMが存在しなくなってしまっている。
    // useEffectでのフラグ変更後のNULLでもだめ。なので以下の対応。
    // 300ミリ秒待機してからドロワーの内容をnullに設定
    // 上記の問題はドロワーコンテンツのコンポーネントのuseEffectでsetDrawerComponentを呼べば
    // このようなHACKは不要になると思われるがコンテンツのコンポーネント側の実装がその分増えるので
    // とりあえずはこれでいい気がする
    await new Promise((resolve) => setTimeout(resolve, 300));
    setDrawerComponent(null);
  };

  useEffect(() => {
    if (context.overlapLevel === 0) {
      document.body.classList.remove('overflow-hidden');
    }
  }, [context.overlapLevel]);

  useEffect(() => {
    if (isOpen) {
      context.handleIncrementOverlapLevel();
      // bodyにクラスを設定
      document.body.classList.add('overflow-hidden');
    } else {
      context.handleDecrementOverlapLevel();
    }
  }, [isOpen]);

  return (
    <SdHeaderMenuDrawerContext.Provider value={{ isOpen, openDrawer, closeDrawer: () => void closeDrawer() }}>
      {children}
      <SdHeaderDrawerFromLeft isOpen={isOpen} drawerComponent={drawerComponent} onCloseDrawer={closeDrawer} />
    </SdHeaderMenuDrawerContext.Provider>
  );
};

export const useSdHeaderMenuDrawer = () => {
  const context = useContext(SdHeaderMenuDrawerContext);
  if (!context) {
    throw new Error('useSdHeaderMenuDrawer must be used within a DrawerProvider');
  }
  return context;
};
